// Security tab of the advertiser Settings surface.
//
// Set / change the account password. Identity at auth.users is shared with
// the CTV Buyer dashboard, so a password set here works for both surfaces.
//
// Writes `raw_user_meta_data.password_set: true` alongside the password so
// the backend has-password signal reliably reports the change (see
// hasUserSetPassword in selfservice-api's email-dispatch.controller).
//
// This component is a plain section — the parent SettingsLayout owns the
// PublicHeader, page heading, and tab strip.

import React from 'react';
import { useParams } from 'react-router-dom';
import { Lock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { selfServiceSupabase } from '../../lib/selfServiceSupabase';
import { usePublisherConfig } from '../../data/publisherConfigs';
import PrimaryButton from '../../components/self-service/shared/PrimaryButton';

const SettingsSecurityTab: React.FC = () => {
  const { user, refreshHasPassword } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const { config } = usePublisherConfig(slug);
  const accent = config?.accentColor || config?.primaryColor;
  const primary = config?.primaryColor;
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successAt, setSuccessAt] = React.useState<Date | null>(null);

  const passwordsValid = password.length >= 8 && password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selfServiceSupabase) {
      setError('Supabase Auth is not configured.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError(null);
    setSuccessAt(null);
    setBusy(true);
    try {
      const { error: authErr } = await selfServiceSupabase.auth.updateUser({
        password,
        data: { password_set: true },
      });
      if (authErr) throw authErr;
      setSuccessAt(new Date());
      setPassword('');
      setConfirm('');
      void refreshHasPassword();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password.');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = accent
    ? ({ '--tw-ring-color': accent } as React.CSSProperties)
    : {};

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
        <Lock className="w-4 h-4 text-slate-400" />
        <h2 className="text-sm font-semibold text-slate-900">Password</h2>
      </div>

      <form onSubmit={handleSubmit} className="px-5 py-5 space-y-5">
        <div>
          <label className="block">
            <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
              Email
            </span>
            <input
              type="email"
              value={user?.email ?? ''}
              disabled
              className="mt-1 w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600"
            />
          </label>
        </div>

        <div>
          <label className="block">
            <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
              New password
            </span>
            <div className="mt-1 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                style={inputStyle}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:outline-none"
              />
            </div>
          </label>
        </div>

        <div>
          <label className="block">
            <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
              Confirm new password
            </span>
            <div className="mt-1 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-type your new password"
                style={inputStyle}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:outline-none"
              />
            </div>
          </label>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm bg-rose-50 border border-rose-200 text-rose-900 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successAt && (
          <div className="flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Password updated</div>
              <p>
                You can now sign in to both the wizard and the CTVBuyer dashboard with this
                password.
              </p>
            </div>
          </div>
        )}

        <PrimaryButton
          type="submit"
          disabled={busy || !passwordsValid}
          accentColor={accent}
          primaryColor={primary}
          fullWidth
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? 'Updating…' : 'Update password'}
        </PrimaryButton>
      </form>
    </section>
  );
};

export default SettingsSecurityTab;
